"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";

const STEP_KEYS = [
	{ numKey: "step1Num", titleKey: "step1Title", descKey: "step1Desc" },
	{ numKey: "step2Num", titleKey: "step2Title", descKey: "step2Desc" },
	{ numKey: "step3Num", titleKey: "step3Title", descKey: "step3Desc" },
	{ numKey: "step4Num", titleKey: "step4Title", descKey: "step4Desc" },
	{ numKey: "step5Num", titleKey: "step5Title", descKey: "step5Desc" },
	{ numKey: "step6Num", titleKey: "step6Title", descKey: "step6Desc" },
] as const;

export interface InjectionMoldingProcessProps {
	translationNamespace?: string;
	/** 顶部 CTA 按钮跳转链接 */
	ctaHref?: string;
	/** 6 个步骤对应的图片路径，顺序与 step1～step6 一致 */
	images: [string, string, string, string, string, string];
}

export default function InjectionMoldingProcess({
	translationNamespace = "injectionMoldingProcess",
	ctaHref = "/upload",
	images,
}: InjectionMoldingProcessProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题 + 简介 + 按钮 */}
				<div className="mb-6 flex flex-col gap-6 sm:mb-8 md:mb-10 lg:mb-12 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
					<div>
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>
						<p className="text-sm leading-relaxed text-black md:text-base">{t("intro")}</p>
					</div>
					<a
						href={ctaHref}
						target="_blank"
						rel="noopener noreferrer"
						className="shrink-0 rounded-lg bg-themePrimary400 px-6 py-3.5 text-base font-semibold text-white transition-all duration-300 hover:bg-opacity-90 sm:px-8 sm:py-4"
					>
						{t("ctaButton")}
					</a>
				</div>

				{/* 6 步骤 2x3 网格：文字在上、图片在下，整块灰底 */}
				<div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
					{STEP_KEYS.map((step, index) => (
						<div key={step.titleKey} className="flex flex-col overflow-hidden rounded-lg bg-gray-100">
							{/* 上方：编号 + 标题 + 描述 */}
							<div className="flex flex-col p-5 sm:p-6">
								<p className="mb-1 text-sm font-semibold text-themePrimary400">{t(step.numKey)}</p>
								<h3 className="mb-3 text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(step.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(step.descKey)}</p>
							</div>
							{/* 下方：步骤图 */}
							<div className="relative aspect-square w-full bg-gray-100">
								<Image
									src={images[index]}
									alt={t(step.titleKey)}
									fill
									className="object-fill p-4"
									sizes="(max-width: 640px) 100vw, (max-width: 640px)"
								/>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
